// Entra ID sign-in: authorization code with PKCE, in the browser, no secrets
// (docs/architecture.md § auth). MSAL holds the tokens; the REST client asks
// for one per request and API Gateway's JWT authorizer validates it before any
// handler runs. Roles do NOT come from token claims — the app reads them from
// `GET /me`, so a role change takes effect without a new token.

import {
  PublicClientApplication,
  BrowserAuthError,
  BrowserAuthErrorCodes,
  InteractionRequiredAuthError,
  type AccountInfo,
  type Configuration,
} from '@azure/msal-browser'
import { currentConfig } from './config'

let app: PublicClientApplication | null = null

function msalConfig(): Configuration {
  const { entra } = currentConfig()
  return {
    auth: {
      clientId: entra.client_id,
      authority: `https://login.microsoftonline.com/${entra.tenant_id}`,
      redirectUri: window.location.origin,
      postLogoutRedirectUri: window.location.origin,
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // Session storage, not local: the token dies with the tab, which is the
      // conservative choice for an app that reaches protected health data.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  }
}

/** Creates the MSAL instance and completes any redirect already in flight. */
export async function initAuth(): Promise<PublicClientApplication> {
  if (app) return app
  app = new PublicClientApplication(msalConfig())
  await app.initialize()
  const result = await app.handleRedirectPromise()
  if (result?.account) app.setActiveAccount(result.account)
  else {
    const [first] = app.getAllAccounts()
    if (first) app.setActiveAccount(first)
  }
  return app
}

export function activeAccount(): AccountInfo | null {
  return app?.getActiveAccount() ?? null
}

/** Sends the user to Entra. Resolves only by navigating away. */
export async function signIn(): Promise<void> {
  const instance = await initAuth()
  await instance.loginRedirect({ scopes: [currentConfig().entra.api_scope] })
}

export async function signOut(): Promise<void> {
  const instance = await initAuth()
  await instance.logoutRedirect()
}

async function acquire(scopes: string[]): Promise<string> {
  const instance = await initAuth()
  const account = instance.getActiveAccount()
  if (!account) {
    await instance.loginRedirect({ scopes })
    throw new Error('Sign-in required')
  }
  try {
    const result = await instance.acquireTokenSilent({ account, scopes })
    return result.accessToken
  } catch (e) {
    // `monitor_window_timeout` is the silent-renewal iframe timing out — an
    // expired Entra session with third-party cookies blocked, or a slow
    // network. Seen in the field 2026-08-10: it killed app START ("Could not
    // start the app: monitor_window_timeout") because only
    // InteractionRequiredAuthError fell back. Microsoft's guidance for this
    // code is exactly this fallback (aka.ms/msaljs/browser-errors); the
    // redirect cannot loop silently — it either signs in or stops at Entra.
    const iframeTimedOut =
      e instanceof BrowserAuthError && e.errorCode === BrowserAuthErrorCodes.monitorWindowTimeout
    if (e instanceof InteractionRequiredAuthError || iframeTimedOut) {
      await instance.acquireTokenRedirect({ account, scopes })
      throw new Error('Sign-in required')
    }
    throw e
  }
}

/** The bearer token for the AP API. */
export function apiToken(): Promise<string> {
  return acquire([currentConfig().entra.api_scope])
}

/** The delegated token for the Graph people search behind the staff picker. */
export function graphToken(): Promise<string> {
  const scopes = currentConfig().entra.graph_scopes ?? ['User.ReadBasic.All']
  return acquire(scopes)
}
